import { LabeledInfo } from "./LabeledInfo";
import { mobileDashboardHeaderSx, mobileDashboardActionsSx } from "./dashboardMobileLayout";
import {
  ExpandMoreOutlined,
  ArrowForwardOutlined,
  CloseOutlined,
  EventRepeatOutlined,
  RefreshOutlined,
  ScheduleOutlined,
  TrendingDownOutlined,
  WarningAmberOutlined
} from "@mui/icons-material";
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Card, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, Drawer, FormControl, FormControlLabel,
  Grid, IconButton, InputLabel, MenuItem, Select, Stack, Switch, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, ToggleButton,
  ToggleButtonGroup, Typography
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { HomeDashboardSelector, HomeDashboardView } from "./HomeDashboardSelector";
import { decideReplenishmentProposal, loadReplenishmentExecutions, loadReplenishmentProposal, loadStockRiskV2,
  ReplenishmentProposal, startReplenishmentProposalReview,
  StockRiskV2Evaluation } from "./api/product";

interface Props {
  canRead: boolean;
  canOpenMaterial?: boolean;
  canReviewActions?: boolean;
  canApproveActions?: boolean;
  onChangeView: (view: HomeDashboardView) => void;
  onReviewAction?: (proposalId: string | null) => void;
  onOpenMaterial: (itemId: string) => void;
  operationsLabel?: string;
  showReplenishment?: boolean;
  showExpiryRisk?: boolean;
}

const riskRank: Record<StockRiskV2Evaluation["riskStatus"], number> = {
  CRITICAL: 0, COVERED_LATE: 1, REPLENISH: 2, WATCH: 3, HEALTHY: 4, INACTIVE: 5
};

function quantity(value: number, uom: string) {
  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value)} ${uom}`;
}

function programCode(itemCode: string) {
  return itemCode.match(/^[^-]+-([^-]+)-MAT-/)?.[1] ?? "OTHER";
}

function RiskChip({ status }: { status: StockRiskV2Evaluation["riskStatus"] }) {
  const { t } = useTranslation();
  const color = status === "CRITICAL" || status === "COVERED_LATE" ? "error"
    : status === "REPLENISH" ? "warning" : status === "HEALTHY" ? "success" : "default";
  return <Chip size="small" color={color} label={t(`dashboard.stockRiskV2.status.${status}`)}
    sx={{ minWidth: 92, fontWeight: 850 }} />;
}

function ProjectionChart({ row }: { row: StockRiskV2Evaluation }) {
  const { t, i18n } = useTranslation();
  const points = row.projection;
  const values = [
    ...points.map((point) => Number(point.closingQuantity)),
    ...row.events.map((event) => Number(event.closingQuantity))
  ];
  const ceiling = Math.max(row.policy.maximumQuantity, ...values, 1);
  const floor = Math.min(0, ...values);
  const range = Math.max(1, ceiling - floor);
  const width = 720;
  const height = 205;
  const left = 52;
  const right = 12;
  const top = 18;
  const bottom = 34;
  const plotWidth = width - left - right;
  const plotHeight = height - top - bottom;
  const x = (index: number) => left + (points.length <= 1 ? 0 : index * plotWidth / (points.length - 1));
  const y = (value: number) => top + (ceiling - value) * plotHeight / range;
  const indexByDate = new Map(points.map((point, index) => [point.date, index]));
  let running = Number(row.availableQuantity);
  let stepPath = `M ${left} ${y(running)}`;
  points.forEach((point, index) => {
    stepPath += ` H ${x(index)}`;
    row.events.filter((event) => event.date === point.date).forEach((event) => {
      running = Number(event.closingQuantity);
      stepPath += ` V ${y(running)}`;
    });
  });
  stepPath += ` H ${width - right}`;
  const dateLabel = (value: string) => new Date(`${value}T12:00:00`).toLocaleDateString(
    i18n.language.startsWith("en") ? "en-US" : "fr-FR", { month: "short", day: "2-digit" }
  );
  const threshold = (value: number, color: string, label: string) => <g>
    <line x1={left} y1={y(value)} x2={width - right} y2={y(value)} stroke={color}
      strokeDasharray="5 4" strokeWidth="1" />
    <text x="3" y={y(value) + 4} fontSize="10" fill={color}>{label}</text>
  </g>;

  return <Box sx={{ height: 215, border: 1, borderColor: "divider", borderRadius: 1,
    bgcolor: "grey.50", overflow: "hidden" }}>
    <svg viewBox={`0 0 ${width} ${height}`} width="100%" height="100%"
      aria-label={t("dashboard.stockRiskV2.detail.chartLabel")}>
      {threshold(row.policy.maximumQuantity, "#65758b", `${row.policy.maximumQuantity} max`)}
      {threshold(row.policy.minimumQuantity, "#ed6c02", `${row.policy.minimumQuantity} ROP`)}
      {threshold(row.policy.safetyStockQuantity, "#d32f2f", `${row.policy.safetyStockQuantity} safety`)}
      {threshold(0, "#b3261e", "0")}
      <path d={stepPath} fill="none" stroke="#0575a8" strokeWidth="3"
        strokeLinejoin="round" strokeLinecap="round" />
      {row.events.filter((event) => event.type !== "FORECAST_CONSUMPTION")
        .map((event, eventIndex) => {
        const pointIndex = indexByDate.get(event.date);
        if (pointIndex === undefined) return null;
        const positive = event.type === "CONFIRMED_SUPPLY";
        const color = positive ? "#14866d"
          : event.type === "FORECAST_CONSUMPTION" ? "#ed6c02" : "#d32f2f";
        const sameDayIndex = row.events.slice(0, eventIndex)
          .filter((candidate) => candidate.date === event.date).length;
        return <g key={`${event.type}-${event.date}-${eventIndex}`}>
          <line x1={x(pointIndex)} y1={top} x2={x(pointIndex)} y2={height - bottom}
            stroke={color} strokeWidth="1.5" strokeDasharray="4 3" />
          <circle cx={x(pointIndex)} cy={y(Number(event.closingQuantity))} r="4" fill={color} />
          <text x={Math.min(x(pointIndex) + 4, width - 95)}
            y={Math.max(top + 11, y(Number(event.closingQuantity)) - 7 - sameDayIndex * 12)}
            fontSize="10" fontWeight="700" fill={color}>
            {positive ? "+" : "−"}{quantity(event.quantity, row.policy.baseUom)}
          </text>
        </g>;
      })}
      {points.length > 0 && <>
        <text x={left} y={height - 8} fontSize="10" fill="#68727d">{dateLabel(points[0].date)}</text>
        <text x={width - 55} y={height - 8} fontSize="10" fill="#68727d">
          {dateLabel(points[points.length - 1].date)}
        </text>
      </>}
    </svg>
  </Box>;
}

export function StockoutRiskV2Dashboard({
  canRead, canOpenMaterial = true, canReviewActions = false, canApproveActions = false,
  onChangeView, onReviewAction, onOpenMaterial, operationsLabel,
  showReplenishment = true, showExpiryRisk = false
}: Props) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [horizon, setHorizon] = useState(30);
  const [includeForecast, setIncludeForecast] = useState(false);
  const [warehouse, setWarehouse] = useState("ALL");
  const [program, setProgram] = useState("ALL");
  const [supplier, setSupplier] = useState("ALL");
  const [riskFilter, setRiskFilter] = useState("ACTIONABLE");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [proposal, setProposal] = useState<ReplenishmentProposal | null>(null);
  const [decisionQuantity, setDecisionQuantity] = useState("");
  const [decisionReason, setDecisionReason] = useState("");
  const risk = useQuery({
    queryKey: ["material-stock-risk-v2", horizon, includeForecast],
    queryFn: () => loadStockRiskV2(horizon, includeForecast), enabled: canRead,
    refetchInterval: 60_000
  });
  const executions = useQuery({
    queryKey: ["replenishment-executions"],
    queryFn: loadReplenishmentExecutions,
    enabled: canRead,
    refetchInterval: 60_000
  });
  const allRows = useMemo(() => [...(risk.data ?? [])].sort((a, b) =>
    riskRank[a.riskStatus] - riskRank[b.riskStatus]
      || a.policy.itemCode.localeCompare(b.policy.itemCode)), [risk.data]);
  const warehouses = [...new Set(allRows.map((row) => row.policy.warehouseCode))];
  const programs = [...new Set(allRows.map((row) => programCode(row.policy.itemCode)))];
  const suppliers = [...new Set(allRows.map((row) => row.supplierDisplayName).filter(Boolean))] as string[];
  const rows = allRows.filter((row) => (warehouse === "ALL" || row.policy.warehouseCode === warehouse)
    && (program === "ALL" || programCode(row.policy.itemCode) === program)
    && (supplier === "ALL" || row.supplierDisplayName === supplier)
    && (riskFilter === "ALL" ? true : riskFilter === "ACTIONABLE"
      ? !["HEALTHY", "INACTIVE"].includes(row.riskStatus) : row.riskStatus === riskFilter));
  const selected = rows.find((row) => row.policy.id === selectedId) ?? null;
  const activeExecutions = (executions.data ?? [])
    .filter((execution) => !["CLOSED", "CANCELLED"].includes(execution.status));
  const activeExecutionsByProposalId = new Map(activeExecutions
    .map((execution) => [execution.proposalId, execution]));
  const activeExecutionsByMaterialWarehouse = new Map(activeExecutions
    .map((execution) => [`${execution.itemId}:${execution.warehouseCode}`, execution]));
  const executionFor = (row: StockRiskV2Evaluation) =>
    (row.proposalId ? activeExecutionsByProposalId.get(row.proposalId) : undefined)
    ?? activeExecutionsByMaterialWarehouse.get(`${row.policy.itemId}:${row.policy.warehouseCode}`);
  const launch = rows.filter((row) => row.actionType === "LAUNCH_REPLENISHMENT"
    && !executionFor(row));
  const followUp = rows.filter((row) => Boolean(executionFor(row)));
  const actionable = [...launch, ...followUp];
  const expedite = rows.filter((row) => row.actionType === "EXPEDITE");
  const updatedTime = new Date(risk.dataUpdatedAt || Date.now()).toLocaleTimeString(
    i18n.language.startsWith("en") ? "en-US" : "fr-FR", { hour: "2-digit", minute: "2-digit" }
  );
  const date = (value: string | null) => value ? new Date(`${value}T12:00:00`).toLocaleDateString(
    i18n.language.startsWith("en") ? "en-US" : "fr-FR", { month: "short", day: "2-digit" }
  ) : "—";
  const actionDate = (value: string | null) => value && value < new Date().toISOString().slice(0, 10)
    ? t("dashboard.stockRiskV2.detail.immediate") : date(value);
  const dateTime = (value: string | null) => value
    ? new Date(value).toLocaleString(i18n.language.startsWith("en") ? "en-US" : "fr-FR") : "—";
  const review = useMutation({
    mutationFn: (row: StockRiskV2Evaluation) => row.proposalId
      ? loadReplenishmentProposal(row.proposalId)
      : startReplenishmentProposalReview(row.policy.id),
    onSuccess: (created) => {
      setProposal(created);
      setDecisionQuantity(String(created.recommendedQuantity));
      setDecisionReason("");
    }
  });
  const decision = useMutation({
    mutationFn: (nextDecision: "APPROVED" | "REJECTED") =>
      decideReplenishmentProposal(proposal!.id, {
        quantity: Number(decisionQuantity), decision: nextDecision,
        reason: decisionReason.trim(), version: proposal!.version
      }),
    onSuccess: async () => {
      setProposal(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["material-stock-risk-v2"] }),
        queryClient.invalidateQueries({ queryKey: ["replenishment-executions"] })
      ]);
    }
  });

  return <Stack spacing={1.1} sx={{
    width: "100%", "@media (max-width: 599.95px)": { "& > *": { flexShrink: 0 } },
    height: { xs: "auto", sm: "100%" },
    minHeight: 0,
    overflow: { xs: "visible", sm: "hidden" }
  }}>
    <Stack sx={mobileDashboardHeaderSx} direction={{ xs: "column", md: "row" }} justifyContent="space-between"
      alignItems={{ xs: "stretch", md: "center" }} spacing={1}>
      <Box>
        <Typography variant="h4" component="h1" fontWeight={850}
          sx={{ fontSize: { xs: "1.4rem", md: "1.75rem" } }}>
          {t("dashboard.stockRiskV2.title")}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t("dashboard.stockRiskV2.subtitle")}
        </Typography>
      </Box>
      <Stack sx={mobileDashboardActionsSx} direction="row" spacing={1} alignItems="center">
        <HomeDashboardSelector value="stock-risk-v2" operationsLabel={operationsLabel}
          showStockRiskV2 showExpiryRisk={showExpiryRisk}
          showReplenishment={showReplenishment} onChange={onChangeView} />
        <IconButton size="small" aria-label={t("dashboard.refresh")}
          onClick={() => void Promise.all([risk.refetch(), executions.refetch()])}><RefreshOutlined /></IconButton>
        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
          {t("dashboard.updated", { time: updatedTime })}
        </Typography>
      </Stack>
    </Stack>

    <Card variant="outlined" sx={{ px: 1.25, pt: 1.35, pb: 1, flexShrink: 0, overflow: "visible" }}>
      <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap"
        sx={theme => ({ [theme.breakpoints.down("sm")]: {
          display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1.25,
          "& > .MuiToggleButtonGroup-root": { gridColumn: "1 / -1", width: "100%",
            "& > button": { flex: 1, minWidth: 0, minHeight: 44 } },
          "& > .MuiFormControlLabel-root": { gridColumn: "1 / -1", mx: 0, minHeight: 44, whiteSpace: "normal" },
          "& > .MuiFormControl-root": { minWidth: 0, width: "100%",
            "& .MuiInputBase-root": { minHeight: 44 },
            "& .MuiSelect-select": { whiteSpace: "normal", overflowWrap: "anywhere" } },
          "& > .MuiBox-root": { display: "none" },
          "& > .MuiTypography-root": { gridColumn: "1 / -1" }
        }, "@media (max-width: 359.95px)": { gridTemplateColumns: "minmax(0, 1fr)" } })}>
        <ToggleButtonGroup size="small" exclusive value={horizon}
          onChange={(_, value) => value && setHorizon(value)}>
          {[7, 30, 60, 90].map((value) => <ToggleButton key={value} value={value}
            sx={{ px: 1.25, py: .4, fontWeight: 800 }}>{value}d</ToggleButton>)}
        </ToggleButtonGroup>
        <FormControlLabel sx={{ mx: .25, whiteSpace: "nowrap" }}
          control={<Switch size="small" checked={includeForecast}
            onChange={(event) => setIncludeForecast(event.target.checked)} />}
          label={<Typography variant="body2" fontWeight={800}>
            {t("dashboard.stockRiskV2.filters.includeForecast")}
          </Typography>} />
        <FormControl size="small" sx={{ minWidth: 135 }}><InputLabel>{t("dashboard.stockRiskV2.filters.warehouse")}</InputLabel>
          <Select value={warehouse} label={t("dashboard.stockRiskV2.filters.warehouse")} onChange={(event) => setWarehouse(event.target.value)}>
            <MenuItem value="ALL">{t("dashboard.stockRiskV2.filters.all")}</MenuItem>{warehouses.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
          </Select></FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}><InputLabel>{t("dashboard.stockRiskV2.filters.program")}</InputLabel>
          <Select value={program} label={t("dashboard.stockRiskV2.filters.program")} onChange={(event) => setProgram(event.target.value)}>
            <MenuItem value="ALL">{t("dashboard.stockRiskV2.filters.all")}</MenuItem>{programs.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
          </Select></FormControl>
        <FormControl size="small" sx={{ minWidth: 190 }}><InputLabel>{t("dashboard.stockRiskV2.filters.supplier")}</InputLabel>
          <Select value={supplier} label={t("dashboard.stockRiskV2.filters.supplier")} onChange={(event) => setSupplier(event.target.value)}>
            <MenuItem value="ALL">{t("dashboard.stockRiskV2.filters.all")}</MenuItem>{suppliers.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
          </Select></FormControl>
        <FormControl size="small" sx={{ minWidth: 165 }}><InputLabel>{t("dashboard.stockRiskV2.filters.risk")}</InputLabel>
          <Select value={riskFilter} label={t("dashboard.stockRiskV2.filters.risk")} onChange={(event) => setRiskFilter(event.target.value)}>
            <MenuItem value="ACTIONABLE">{t("dashboard.stockRiskV2.filters.actionable")}</MenuItem>
            <MenuItem value="ALL">{t("dashboard.stockRiskV2.filters.all")}</MenuItem>
            {["CRITICAL", "COVERED_LATE", "REPLENISH", "WATCH", "HEALTHY"].map((value) => <MenuItem key={value} value={value}>{t(`dashboard.stockRiskV2.status.${value}`)}</MenuItem>)}
          </Select></FormControl>
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {t("dashboard.stockRiskV2.legend")}
        </Typography>
      </Stack>
    </Card>

    {!canRead && <Alert severity="warning">{t("dashboard.stockRiskV2.noPermission")}</Alert>}
    {risk.isError && <Alert severity="error">{t("dashboard.stockRiskV2.loadError")}</Alert>}

    <Grid container spacing={1.1}>
      {[
        { key: "stockout", value: rows.filter((row) => row.projectedStockoutDate).length, color: "error.main", icon: <WarningAmberOutlined /> },
        { key: "reorder", value: rows.filter((row) => row.reorderPointDate).length, color: "warning.main", icon: <TrendingDownOutlined /> },
        { key: "expedite", value: expedite.length, color: "info.main", icon: <ScheduleOutlined /> },
        { key: "launch", value: launch.length, color: "secondary.main", icon: <EventRepeatOutlined /> }
      ].map(({ key, value, color, icon }) => <Grid key={key} size={{ xs: 6, md: 3 }}>
        <Card variant="outlined" sx={{ px: 1.5, py: .8, borderTop: 3, borderColor: color }}>
          <Stack direction="row" spacing={1.2} alignItems="center"><Box sx={{ color, display: "flex" }}>{icon}</Box><Box>
            <Typography variant="h5" fontWeight={850} lineHeight={1}>{value}</Typography>
            <Typography variant="caption" color="text.secondary">{t(`dashboard.stockRiskV2.metrics.${key}`)}</Typography>
          </Box></Stack>
        </Card>
      </Grid>)}
    </Grid>

    <Grid container spacing={1.1} sx={{ flex: { xs: "none", sm: 1 }, minHeight: 170, overflow: { xs: "visible", sm: "hidden" } }}>
      <Grid size={12} sx={{ minHeight: 0 }}>
        <Card variant="outlined" sx={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <Stack direction="row" justifyContent="space-between" sx={{ px: 1.5, py: .8, borderBottom: 1, borderColor: "divider" }}>
            <Typography fontWeight={850}>{t("dashboard.stockRiskV2.table.title")}</Typography>
            <Typography variant="caption" color="text.secondary">{t("dashboard.stockRiskV2.table.horizon", { count: horizon })}</Typography>
          </Stack>
          {risk.isLoading ? <Box sx={{ p: 5, textAlign: "center" }}><CircularProgress /></Box>
            : !rows.length ? <Alert severity="info" sx={{ m: 1.5 }}>{t("dashboard.stockRiskV2.table.empty")}</Alert>
              : <>
                <Stack spacing={1} sx={{ display: { xs: "flex", sm: "none" }, p: 1, minWidth: 0 }}>
                  {rows.map(row => <Accordion key={row.policy.id} disableGutters elevation={0}
                    onChange={(_, expanded) => { if (expanded) setSelectedId(row.policy.id); }}
                    sx={{ border: 1, borderColor: selected?.policy.id === row.policy.id ? "primary.main" : "divider",
                      borderRadius: 1, overflowWrap: "anywhere", "&::before": { display: "none" } }}>
                    <AccordionSummary expandIcon={<ExpandMoreOutlined />} id={`risk-stock-${row.policy.id}`}
                      aria-controls={`risk-stock-details-${row.policy.id}`}
                      sx={{ px: 1.25, minHeight: 48, "& .MuiAccordionSummary-content": { minWidth: 0 },
                        "& .MuiChip-root": { maxWidth: "100%", height: "auto" }, "& .MuiChip-label": { whiteSpace: "normal", py: .5 } }}>
                      <Stack spacing={.75} sx={{ minWidth: 0 }}>
                        <Typography fontWeight={850}>{row.policy.itemCode}</Typography>
                        <RiskChip status={row.riskStatus} />
                        <LabeledInfo label={t("dashboard.stockRiskV2.columns.stockout")}>{date(row.projectedStockoutDate)}</LabeledInfo>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 1.25, pt: 0 }}>
                      <Stack spacing={1.25}>
                        <LabeledInfo label={t("dashboard.stockRiskV2.columns.material")}>{row.policy.itemDisplayName} · {row.policy.warehouseCode}</LabeledInfo>
                        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1 }}>
                          <LabeledInfo label={t("dashboard.stockRiskV2.columns.available")}>{quantity(row.availableQuantity, row.policy.baseUom)}</LabeledInfo>
                          <LabeledInfo label={t("dashboard.stockRiskV2.columns.demand")}>{quantity(row.firmDemandQuantity, row.policy.baseUom)}</LabeledInfo>
                        </Box>
                        <LabeledInfo label={t("dashboard.stockRiskV2.columns.supply")}>
                          {row.confirmedInboundQuantity ? `+ ${quantity(row.confirmedInboundQuantity, row.policy.baseUom)}` : "—"}
                          <Typography variant="caption" component="div">{row.supplyReference ? `${row.supplyReference} · ` : ""}{row.nextConfirmedInboundAt ? date(row.nextConfirmedInboundAt.slice(0, 10)) : ""}</Typography>
                        </LabeledInfo>
                        <LabeledInfo label={t("dashboard.stockRiskV2.columns.action")}>
                          {t(`dashboard.stockRiskV2.actions.${row.actionType}`)}
                          <Typography variant="caption" component="div">{actionDate(row.recommendedLaunchDate)}</Typography>
                        </LabeledInfo>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>)}
                </Stack>
                <TableContainer sx={{ display: { xs: "none", sm: "block" }, flex: 1, minHeight: 0, overflow: "auto" }}><Table stickyHeader size="small" sx={{ minWidth: 980 }}>
                <TableHead><TableRow>{["material", "risk", "available", "demand", "supply", "stockout", "action"].map((key) =>
                  <TableCell key={key} align={["available", "demand"].includes(key) ? "right" : "left"}
                    sx={{ py: .7, fontWeight: 850 }}>{t(`dashboard.stockRiskV2.columns.${key}`)}</TableCell>)}</TableRow></TableHead>
                <TableBody>{rows.map((row) => <TableRow key={row.policy.id} hover selected={selected?.policy.id === row.policy.id}
                  onClick={() => setSelectedId(row.policy.id)} sx={{ cursor: "pointer" }}>
                  <TableCell sx={{ py: .7 }}><Typography variant="body2" fontWeight={850}>{row.policy.itemCode}</Typography><Typography variant="caption" color="text.secondary">{row.policy.itemDisplayName} · {row.policy.warehouseCode}</Typography></TableCell>
                  <TableCell><RiskChip status={row.riskStatus} /></TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>{quantity(row.availableQuantity, row.policy.baseUom)}</TableCell>
                  <TableCell align="right">{quantity(row.firmDemandQuantity, row.policy.baseUom)}</TableCell>
                  <TableCell><Typography variant="body2">{row.confirmedInboundQuantity ? `+ ${quantity(row.confirmedInboundQuantity, row.policy.baseUom)}` : "—"}</Typography><Typography variant="caption" color="text.secondary">{row.supplyReference ? `${row.supplyReference} · ` : ""}{row.nextConfirmedInboundAt ? date(row.nextConfirmedInboundAt.slice(0, 10)) : ""}</Typography></TableCell>
                  <TableCell>{date(row.projectedStockoutDate)}</TableCell>
                  <TableCell><Typography variant="body2" fontWeight={800}>{t(`dashboard.stockRiskV2.actions.${row.actionType}`)}</Typography><Typography variant="caption" color="text.secondary">{actionDate(row.recommendedLaunchDate)}</Typography></TableCell>
                </TableRow>)}</TableBody>
              </Table></TableContainer></>}
        </Card>
      </Grid>
    </Grid>

    <Card variant="outlined" sx={{
      overflow: "hidden",
      flexShrink: 0,
      height: { xs: "auto", md: "clamp(190px, 25vh, 280px)" },
      display: "flex",
      flexDirection: "column"
    }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center"
        sx={{ px: 1.5, py: .8, borderBottom: 1, borderColor: "divider" }}>
        <Box>
          <Typography fontWeight={850}>{t("dashboard.stockRiskV2.recommendations.title")}</Typography>
          <Typography variant="caption" color="text.secondary">
            {t("dashboard.stockRiskV2.recommendations.subtitle")}
          </Typography>
        </Box>
        <Chip size="small" label={actionable.length}
          color={actionable.length ? "warning" : "success"} />
      </Stack>
      {!actionable.length ? <Alert severity="success" sx={{ m: 1.25 }}>
        {t("dashboard.stockRiskV2.recommendations.empty")}
      </Alert> : <Grid container sx={{ flex: 1, minHeight: 0 }}>
        <Grid size={{ xs: 12, lg: 6 }} sx={{
          borderRight: { lg: 1 }, borderColor: "divider",
          display: "flex", flexDirection: "column", minHeight: 0
        }}>
          <Typography variant="caption" fontWeight={850} color="text.secondary"
            sx={{ display: "block", px: 1.5, py: .7, bgcolor: "action.hover" }}>
            {t("dashboard.stockRiskV2.recommendations.proposals")} ({launch.length})
          </Typography>
          <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          {!launch.length ? <Typography variant="caption" color="text.secondary"
            sx={{ display: "block", p: 1.5 }}>{t("dashboard.stockRiskV2.recommendations.noProposals")}</Typography>
            : launch.map((row) => <Stack key={row.policy.id} direction="row" spacing={1}
              alignItems="center" sx={{ px: 1.5, py: 1, borderTop: 1, borderColor: "divider" }}>
              <RiskChip status={row.riskStatus} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={850} noWrap>
                  {row.policy.itemCode} · {row.policy.itemDisplayName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  + {quantity(row.recommendedActionQuantity, row.policy.baseUom)} · {actionDate(row.recommendedLaunchDate)}
                </Typography>
              </Box>
              {canReviewActions && <Button size="small" variant="outlined"
                endIcon={<ArrowForwardOutlined />} disabled={review.isPending}
                color={row.proposalStatus === "APPROVED" ? "success"
                  : row.proposalStatus === "REJECTED" ? "error" : "primary"}
                onClick={() => review.mutate(row)}>
                {row.proposalStatus
                  ? t(`dashboard.stockRisk.review.status.${row.proposalStatus}`)
                  : t("dashboard.stockRiskV2.recommendations.reviewProposal")}
              </Button>}
            </Stack>)}
          </Box>
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }} sx={{
          display: "flex", flexDirection: "column", minHeight: 0
        }}>
          <Typography variant="caption" fontWeight={850} color="text.secondary"
            sx={{ display: "block", px: 1.5, py: .7, bgcolor: "action.hover" }}>
            {t("dashboard.stockRiskV2.recommendations.followUp")} ({followUp.length})
          </Typography>
          <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          {!followUp.length ? <Typography variant="caption" color="text.secondary"
            sx={{ display: "block", p: 1.5 }}>{t("dashboard.stockRiskV2.recommendations.noFollowUp")}</Typography>
            : followUp.map((row) => <Stack key={row.policy.id} direction="row" spacing={1}
              alignItems="center" sx={{ px: 1.5, py: 1, borderTop: 1, borderColor: "divider" }}>
              <RiskChip status={row.riskStatus} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={850} noWrap>
                  {row.policy.itemCode} · {row.policy.itemDisplayName}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap display="block">
                  {t(`dashboard.stockRiskV2.actions.${row.actionType}`)} · {row.supplyReference ?? "—"}
                </Typography>
              </Box>
              {canReviewActions && onReviewAction && <Button size="small" variant="outlined"
                endIcon={<ArrowForwardOutlined />}
                onClick={() => onReviewAction(executionFor(row)!.proposalId)}>
                {t("dashboard.stockRiskV2.recommendations.openFollowUp")}
              </Button>}
            </Stack>)}
          </Box>
        </Grid>
      </Grid>}
    </Card>
    {review.isError && <Alert severity="error">{t("dashboard.stockRisk.review.loadError")}</Alert>}

    <Drawer anchor="right" open={Boolean(selected)} onClose={() => setSelectedId(null)}
      PaperProps={{ sx: { width: { xs: "94vw", sm: 660 }, maxWidth: "100%", p: 2 } }}>
      {selected && <Stack spacing={1.5}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "flex-start" }}
            spacing={1}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" fontWeight={850}>{selected.policy.itemCode}</Typography>
              <Typography color="text.secondary">{selected.policy.itemDisplayName}</Typography>
              <Typography variant="caption" color="text.secondary">{selected.supplierDisplayName ?? "—"} · {selected.policy.warehouseCode}</Typography>
            </Box>
            <Stack spacing={0.75} alignItems={{ xs: "stretch", sm: "flex-end" }}>
              <Stack direction="row" spacing={.5} alignItems="center" justifyContent="flex-end">
                <RiskChip status={selected.riskStatus} />
                <IconButton aria-label={t("common.actions.close")} onClick={() => setSelectedId(null)}><CloseOutlined /></IconButton>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={horizon}
                  onChange={(_, value) => value && setHorizon(value)}
                  aria-label={t("dashboard.stockRiskV2.title")}
                >
                  {[7, 30, 60, 90].map((days) => (
                    <ToggleButton key={days} value={days} sx={{ px: 1.25, py: 0.35, fontWeight: 800 }}>
                      {days}D
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={(
                    <Switch
                      size="small"
                      checked={includeForecast}
                      onChange={(event) => setIncludeForecast(event.target.checked)}
                    />
                  )}
                  label={<Typography variant="caption" fontWeight={700}>{t("dashboard.stockRiskV2.filters.includeForecast")}</Typography>}
                />
              </Stack>
            </Stack>
          </Stack>
        <ProjectionChart row={selected} />
        <Grid container spacing={.7}>{[
          ["reorder", date(selected.reorderPointDate)], ["stockout", date(selected.projectedStockoutDate)],
          ["recovery", date(selected.recoveryDate)], ["deficit", quantity(selected.maximumDeficitQuantity, selected.policy.baseUom)]
        ].map(([key, value]) => <Grid key={key} size={{ xs: 6, sm: 3 }}><Box sx={{ bgcolor: "grey.100", borderRadius: 1, p: .8, height: "100%" }}><Typography variant="caption" color="text.secondary" display="block">{t(`dashboard.stockRiskV2.detail.${key}`)}</Typography><Typography variant="body2" fontWeight={800}>{value}</Typography></Box></Grid>)}</Grid>
        {includeForecast && selected.averageDailyConsumption > 0 && <Alert severity="info">
          {t("dashboard.stockRiskV2.detail.forecastSummary", {
            daily: quantity(selected.averageDailyConsumption, `${selected.policy.baseUom}/${t("dashboard.stockRisk.day")}`),
            total: quantity(selected.averageDailyConsumption * horizon, selected.policy.baseUom),
            count: horizon
          })}
        </Alert>}
        {selected.events.some((event) => event.type !== "FORECAST_CONSUMPTION") && <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
          <Typography variant="subtitle2" fontWeight={850} sx={{ px: 1.25, py: .8 }}>
            {t("dashboard.stockRiskV2.detail.eventsTitle")}
          </Typography>
          <Stack sx={{ maxHeight: 220, overflowY: "auto", position: "relative",
            "&::before": { content: '""', position: "absolute",
              // Row padding + date width + marker margin + half the marker width.
              left: (theme) => `calc(${theme.spacing(1.25)} + 62px + ${theme.spacing(1.1)} + 6.5px)`,
              transform: "translateX(-50%)", top: 16, bottom: 16,
              width: 2, bgcolor: "divider" } }}>
            {selected.events.filter((event) => event.type !== "FORECAST_CONSUMPTION").map((event, index) => {
              const positive = event.type === "CONFIRMED_SUPPLY";
              const isCause = selected.primaryStockoutCause?.date === event.date
                && selected.primaryStockoutCause?.type === event.type;
              const color = positive ? "success.main"
                : event.type === "FORECAST_CONSUMPTION" ? "warning.main" : "error.main";
              return <Stack key={`${event.type}-${event.date}-${index}`} direction="row"
                alignItems="flex-start" sx={{ px: 1.25, py: .85, minHeight: 54 }}>
                <Typography variant="caption" fontWeight={750} color="text.secondary"
                  sx={{ width: 62, flexShrink: 0, pt: .15 }}>{date(event.date)}</Typography>
                <Box sx={{ mt: .35, mx: 1.1, width: 13, height: 13, borderRadius: "50%", flexShrink: 0,
                  bgcolor: "background.paper", border: 3, borderColor: color, zIndex: 1 }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  {isCause && <Typography variant="caption" color="error.main" fontWeight={850} display="block">
                    {t("dashboard.stockRiskV2.detail.causeTitle")}
                  </Typography>}
                  <Typography variant="body2">
                    {t(`dashboard.stockRiskV2.detail.event.${event.type}`, {
                      reference: event.reference ?? "—"
                    })}
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight={850} color={color}
                  sx={{ whiteSpace: "nowrap" }}>
                  {positive ? "+" : "−"}{quantity(event.quantity, selected.policy.baseUom)}
                </Typography>
              </Stack>;
            })}
          </Stack>
        </Box>}
        <Alert severity={selected.actionType === "EXPEDITE" ? "warning" : selected.actionType === "NONE" ? "success" : "info"}>
          <Typography variant="body2" fontWeight={750}>{t(`dashboard.stockRiskV2.actionHelp.${selected.actionType}`, { reference: selected.supplyReference ?? "—", count: selected.supplyGapDays ?? 0 })}</Typography>
        </Alert>
        {(canOpenMaterial || canReviewActions) && <Stack direction={{ xs: "column", sm: "row" }} justifyContent="flex-end" spacing={1}>
          {canOpenMaterial && <Button variant="outlined" onClick={() => onOpenMaterial(selected.policy.itemId)}>{t("dashboard.stockRiskV2.detail.openMaterial")}</Button>}
          {selected.actionType === "LAUNCH_REPLENISHMENT" && canReviewActions && <Button variant="contained"
            endIcon={<ArrowForwardOutlined />} disabled={review.isPending}
            onClick={() => review.mutate(selected)}>{t("dashboard.stockRiskV2.recommendations.reviewProposal")}</Button>}
          {executionFor(selected)
            && canReviewActions && onReviewAction && <Button variant="contained"
              endIcon={<ArrowForwardOutlined />}
              onClick={() => onReviewAction(executionFor(selected)!.proposalId)}>{t("dashboard.stockRiskV2.recommendations.openFollowUp")}</Button>}
        </Stack>}
      </Stack>}
    </Drawer>
    <Dialog open={proposal != null} onClose={() => setProposal(null)} fullWidth maxWidth="md">
      {proposal && <>
        <DialogTitle>{t("dashboard.stockRisk.review.title")} · {proposal.itemCode}
          <Typography variant="body2" color="text.secondary">
            {proposal.itemDisplayName} · {proposal.warehouseCode}
          </Typography>
        </DialogTitle>
        <DialogContent dividers><Stack spacing={2}>
          <Stack direction="row" spacing={1}>
            <Chip size="small" color={proposal.evaluationStatus === "CRITICAL" ? "error" : "warning"}
              label={t(`dashboard.stockRisk.status.${proposal.evaluationStatus}`)} />
            <Chip size="small" label={t(`dashboard.stockRisk.review.status.${proposal.status}`)} />
          </Stack>
          <Grid container spacing={1.5}>{[
            ["physical", proposal.onHandQuantity], ["unavailable", proposal.unavailableQuantity],
            ["reserved", proposal.reservedQuantity], ["available", proposal.availableQuantity],
            ["inbound", proposal.confirmedInboundQuantity], ["consumption", proposal.averageDailyConsumption]
          ].map(([key, value]) => <Grid key={String(key)} size={{ xs: 6, md: 4 }}>
            <Typography variant="caption" color="text.secondary">
              {t(`dashboard.stockRisk.review.fields.${key}`)}
            </Typography>
            <Typography fontWeight={850}>{quantity(Number(value), proposal.baseUom)}</Typography>
          </Grid>)}</Grid>
          <Divider />
          <Grid container spacing={1.5}>{[
            ["safety", proposal.safetyStockQuantity], ["minimum", proposal.minimumQuantity],
            ["maximum", proposal.maximumQuantity]
          ].map(([key, value]) => <Grid key={String(key)} size={{ xs: 6, md: 3 }}>
            <Typography variant="caption" color="text.secondary">
              {t(`dashboard.stockRisk.review.fields.${key}`)}
            </Typography>
            <Typography fontWeight={850}>{quantity(Number(value), proposal.baseUom)}</Typography>
          </Grid>)}
            <Grid size={{ xs: 6, md: 3 }}><Typography variant="caption" color="text.secondary">
              {t("dashboard.stockRisk.review.fields.launch")}</Typography>
              <Typography fontWeight={850}>{date(proposal.recommendedLaunchDate)}</Typography></Grid>
          </Grid>
          <Alert severity="warning" icon={<TrendingDownOutlined />}>
            {t("dashboard.stockRisk.review.recommendation", {
              quantity: quantity(proposal.recommendedQuantity, proposal.baseUom)
            })}
          </Alert>
          {proposal.decidedQuantity != null && ["APPROVED", "REJECTED", "ORDERED"].includes(proposal.status)
            && <Alert severity={proposal.status === "REJECTED" ? "error" : "success"}>
              <Typography fontWeight={850}>{t(`dashboard.stockRisk.review.${proposal.status === "REJECTED"
                ? "rejectedQuantity" : "approvedQuantity"}`, {
                quantity: quantity(proposal.decidedQuantity, proposal.baseUom)
              })}</Typography>
              <Typography variant="body2">{t("dashboard.stockRisk.review.decidedAt", {
                date: dateTime(proposal.decidedAt)
              })}</Typography>
              {proposal.decisionReason && <Typography variant="body2">
                {t("dashboard.stockRisk.review.decisionReason", { reason: proposal.decisionReason })}
              </Typography>}
            </Alert>}
          {canApproveActions && proposal.status === "UNDER_REVIEW" && <>
            <Divider />
            <Typography fontWeight={850}>{t("dashboard.stockRisk.review.decisionTitle")}</Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField type="number" label={t("dashboard.stockRisk.review.fields.decidedQuantity")}
                value={decisionQuantity} onChange={(event) => setDecisionQuantity(event.target.value)}
                slotProps={{ htmlInput: { min: 0.000001, step: "any" } }} sx={{ minWidth: 220 }} />
              <TextField fullWidth label={t("dashboard.stockRisk.review.fields.reason")}
                value={decisionReason} onChange={(event) => setDecisionReason(event.target.value)}
                helperText={t("dashboard.stockRisk.review.reasonHelp")} />
            </Stack>
            {decision.isError && <Alert severity="error">{t("dashboard.stockRisk.review.decisionError")}</Alert>}
          </>}
        </Stack></DialogContent>
        <DialogActions>
          {canApproveActions && proposal.status === "UNDER_REVIEW" && <Button color="error"
            disabled={decision.isPending || !decisionReason.trim() || !(Number(decisionQuantity) > 0)}
            onClick={() => decision.mutate("REJECTED")}>{t("dashboard.stockRisk.review.reject")}</Button>}
          {canApproveActions && proposal.status === "UNDER_REVIEW" && <Button color="success" variant="contained"
            disabled={decision.isPending || !(Number(decisionQuantity) > 0)}
            onClick={() => decision.mutate("APPROVED")}>{t("dashboard.stockRisk.review.approve")}</Button>}
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setProposal(null)}>{t("common.actions.close")}</Button>
          {canOpenMaterial && <Button variant="outlined" onClick={() => {
            const itemId = proposal.itemId; setProposal(null); onOpenMaterial(itemId);
          }}>{t("dashboard.stockRisk.review.openMaterial")}</Button>}
        </DialogActions>
      </>}
    </Dialog>
  </Stack>;
}
